'use client';

import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import NextLink from 'next/link';
import NextImage from 'next/image';
import {
  CalendarBlank,
  Clock,
  Eye,
  Tag,
  CaretLeft,
  ShareNetwork,
  Article,
  ArrowLeft,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Header, Footer } from '@/components/layout';
import { blogApi, type BlogPostPreview, type BlogPostFull } from '@/lib/api/blog';
import { siteConfig } from '@/config/site';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function RelatedCard({ post }: { post: BlogPostPreview }) {
  return (
    <NextLink href={`/blog/${post.slug}`}
      className="group block rounded-card overflow-hidden bg-[var(--bg-elevated)] border border-[var(--border-default)] hover:border-[var(--primary)]/30 hover:shadow-float transition-all duration-300">
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-secondary)]">
        {post.image_url ? (
          <NextImage src={post.image_url} alt={post.title} fill
            className="object-cover transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 33vw" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Article className="w-8 h-8 text-[var(--text-tertiary)]" />
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading font-bold text-sm text-[var(--text-primary)] group-hover:text-[var(--primary)] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-xs text-[var(--text-tertiary)] mt-2">
          {formatDate(post.published_at)}
        </p>
      </div>
    </NextLink>
  );
}

// Fixed skeleton widths — avoids Math.random() hydration mismatch
const SKELETON_WIDTHS = ['92%', '78%', '85%', '96%', '71%', '88%', '82%', '74%'] as const;

interface Props {
  slug: string;
  initialPost: BlogPostFull | null;
}

export function BlogPostClient({ slug, initialPost }: Props) {
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => blogApi.getBySlug(slug),
    staleTime: 60000,
    enabled: !!slug,
    initialData: initialPost ?? undefined,
  });

  const handleShare = async () => {
    const url = `${siteConfig.url}/blog/${slug}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: post?.title, url });
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
    }
  };

  return (
    <main className={cn('min-h-screen bg-[var(--bg-primary)]')}>
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        <NextLink href="/blog"
          className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Назад к блогу
        </NextLink>

        {isLoading ? (
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[var(--surface-secondary)] rounded w-3/4" />
            <div className="h-4 bg-[var(--surface-secondary)] rounded w-1/2" />
            <div className="aspect-[2/1] bg-[var(--surface-secondary)] rounded-card" />
            <div className="space-y-3">
              {SKELETON_WIDTHS.map((w, i) => (
                <div key={i} className="h-4 bg-[var(--surface-secondary)] rounded" style={{ width: w }} />
              ))}
            </div>
          </div>
        ) : isError || !post ? (
          <div className="text-center py-20">
            <Article className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
            <p className="text-base text-[var(--text-secondary)]">Статья не найдена</p>
            <NextLink href="/blog" className="inline-flex items-center gap-2 mt-4 text-[var(--primary)]">
              <CaretLeft className="w-4 h-4" /> Вернуться к блогу
            </NextLink>
          </div>
        ) : (
          <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <NextLink key={tag} href={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-pill text-xs font-medium bg-[var(--primary)]/10 text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors">
                    <Tag className="w-3 h-3" /> {tag}
                  </NextLink>
                ))}
              </div>
            )}

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-[var(--text-primary)] leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 mt-4 text-sm text-[var(--text-tertiary)]">
              <span className="flex items-center gap-1.5">
                <CalendarBlank className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              {post.reading_time > 0 && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {post.reading_time} мин чтения
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {post.view_count}
              </span>
              <button onClick={handleShare}
                className="flex items-center gap-1.5 hover:text-[var(--primary)] transition-colors ml-auto">
                <ShareNetwork className="w-4 h-4" /> Поделиться
              </button>
            </div>

            {post.image_url && (
              <div className="relative aspect-[2/1] rounded-card overflow-hidden mt-8 border border-[var(--border-default)]">
                <NextImage src={post.image_url} alt={post.title} fill
                  className="object-cover" sizes="(max-width: 768px) 100vw, 800px" priority />
              </div>
            )}

            <div
              className="mt-10 prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-[var(--text-primary)]
                prose-p:text-[var(--text-secondary)] prose-p:leading-relaxed
                prose-a:text-[var(--primary)] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-[var(--text-primary)]
                prose-img:rounded-card prose-img:border prose-img:border-[var(--border-default)]
                prose-blockquote:border-l-[var(--primary)] prose-blockquote:bg-[var(--primary)]/5 prose-blockquote:rounded-r-card prose-blockquote:py-1 prose-blockquote:px-4
                prose-code:text-[var(--primary)] prose-code:bg-[var(--primary)]/10 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5
                prose-pre:bg-[var(--surface)] prose-pre:border prose-pre:border-[var(--border-default)] prose-pre:rounded-card
                prose-li:text-[var(--text-secondary)]
                prose-hr:border-[var(--border-default)]"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {post.related && post.related.length > 0 && (
              <div className="mt-16 pt-10 border-t border-[var(--border-default)]">
                <h2 className="t-section-title font-heading font-bold text-[var(--text-primary)] mb-6">
                  Похожие статьи
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {post.related.map(p => (
                    <RelatedCard key={p.id} post={p} />
                  ))}
                </div>
              </div>
            )}
          </motion.article>
        )}
      </div>

      <Footer className="bg-[var(--bg-primary)]" />
    </main>
  );
}
